import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiRefreshCw,
  FiSearch,
  FiX,
  FiCheckCircle,
  FiXCircle,
  FiMessageSquare,
  FiPaperclip,
} from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import {
  adminMockCreditsAPI,
  MockCreditPurchaseRequest,
} from '../services/api';
import { getMockCreditAttachmentUrl } from '../config/api';
import { notifyMockCreditTicketsUpdated } from '../utils/adminMockCreditEvents';
import './MockCreditManagement.css';
import './MockCreditPanel.css';
import './Dashboard.css';

interface MockCreditManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso || '—';
  }
}

const MockCreditManagement: React.FC<MockCreditManagementProps> = ({ onLogout, userEmail }) => {
  const { isCollapsed } = useSidebar();
  const [requests, setRequests] = useState<MockCreditPurchaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [awaitingFilter, setAwaitingFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<MockCreditPurchaseRequest | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [replyFile, setReplyFile] = useState<File | null>(null);
  const [grantedCredits, setGrantedCredits] = useState(1);
  const [resolutionNote, setResolutionNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showApproveForm, setShowApproveForm] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminMockCreditsAPI.listRequests({
        status: statusFilter,
        awaiting_user: awaitingFilter || undefined,
        search: search.trim() || undefined,
      });
      setRequests(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, awaitingFilter, search]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const openDetail = async (id: number) => {
    try {
      setError(null);
      const detail = await adminMockCreditsAPI.getRequest(id);
      setSelected(detail);
      setGrantedCredits(detail.requested_credits);
      setResolutionNote('');
      setRejectReason('');
      setReplyBody('');
      setReplyFile(null);
      setShowApproveForm(false);
      setShowRejectForm(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    }
  };

  const handleReply = async () => {
    if (!selected) return;
    if (!replyBody.trim() && !replyFile) {
      setError('Enter a message or attach a file');
      return;
    }
    try {
      setSubmitting(true);
      const updated = await adminMockCreditsAPI.reply(selected.id, replyBody, replyFile);
      setSelected(updated);
      setReplyBody('');
      setReplyFile(null);
      await fetchRequests();
      notifyMockCreditTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    try {
      setSubmitting(true);
      const updated = await adminMockCreditsAPI.approve(selected.id, {
        granted_credits: grantedCredits,
        admin_resolution_note: resolutionNote,
        body: resolutionNote,
      });
      setSelected(updated);
      setShowApproveForm(false);
      await fetchRequests();
      notifyMockCreditTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selected || !rejectReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    try {
      setSubmitting(true);
      const updated = await adminMockCreditsAPI.reject(selected.id, rejectReason, rejectReason);
      setSelected(updated);
      setShowRejectForm(false);
      await fetchRequests();
      notifyMockCreditTicketsUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reject');
    } finally {
      setSubmitting(false);
    }
  };

  const renderAttachment = (reqId: number, att: { id: number; file_name: string; mime_type: string }) => {
    const url = getMockCreditAttachmentUrl(reqId, att.id);
    const isImage = att.mime_type.startsWith('image/');
    return (
      <div key={att.id} className="mock-credits-attachments">
        {url && isImage ? (
          <a href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt={att.file_name} className="mock-credits-attachment-preview" />
          </a>
        ) : null}
        {url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" className="mock-credits-attachment-link">
            {React.createElement(FiPaperclip as React.ComponentType<{ size?: number }>, { size: 14 })}
            {att.file_name}
          </a>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      <div className="dashboard-main">
        <DashboardHeader userEmail={userEmail || 'admin'} organization="NAATI Admin" onLogout={onLogout} />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              className="dashboard-welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <div>
                <h1 className="dashboard-title">Mock credit requests</h1>
                <p className="dashboard-subtitle">
                  Review purchase tickets, reply to users, approve with credits, or reject requests.
                </p>
              </div>
            </motion.div>

            <div className="mock-credit-admin">
            <div className="mock-credit-admin-toolbar">
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="open">Open</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
                <option value="all">All</option>
              </select>
              {statusFilter === 'open' ? (
                <select value={awaitingFilter} onChange={(e) => setAwaitingFilter(e.target.value)}>
                  <option value="">All open</option>
                  <option value="0">Awaiting admin</option>
                  <option value="1">Awaiting user</option>
                </select>
              ) : null}
              <input
                type="search"
                className="mock-credit-admin-search"
                placeholder="Search email, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchRequests()}
              />
              <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={fetchRequests}>
                {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 16 })}
                Search
              </button>
              <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={fetchRequests} disabled={loading}>
                {React.createElement(FiRefreshCw as React.ComponentType<{ size?: number; className?: string }>, {
                  size: 16,
                  className: loading ? 'spinner' : undefined,
                })}
              </button>
            </div>

            {error ? <div className="mock-credits-alert mock-credits-alert-warn">{error}</div> : null}

            <div className="mock-credit-admin-table-wrap">
              {loading ? (
                <p className="mock-credits-tickets-empty">Loading…</p>
              ) : requests.length === 0 ? (
                <p className="mock-credits-tickets-empty">No requests found.</p>
              ) : (
                <table className="mock-credit-admin-table">
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>User</th>
                      <th>Credits</th>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((req) => (
                      <tr key={req.id} onClick={() => openDetail(req.id)}>
                        <td>#{req.id}</td>
                        <td>{req.user_email}</td>
                        <td>{req.requested_credits}{req.granted_credits != null ? ` → ${req.granted_credits}` : ''}</td>
                        <td>{req.contact_phone || req.contact_email || '—'}</td>
                        <td>
                          <span className={`mock-credits-status mock-credits-status-${req.status}`}>
                            {req.status === 'open' && req.awaiting_user ? 'Awaiting user' : req.status}
                          </span>
                        </td>
                        <td>{formatDate(req.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selected ? (
          <motion.div
            className="mock-credits-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !submitting && setSelected(null)}
          >
            <motion.div
              className="mock-credits-modal mock-credits-modal-wide"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mock-credits-modal-header">
                <h3>
                  {React.createElement(FiMessageSquare as React.ComponentType<{ size?: number }>, { size: 20 })}
                  {' '}
                  Ticket #{selected.id} — {selected.user_email}
                </h3>
                <button type="button" className="mock-credits-modal-close" onClick={() => setSelected(null)}>
                  {React.createElement(FiX as React.ComponentType<{ size?: number }>, { size: 22 })}
                </button>
              </div>
              <div className="mock-credits-modal-body">
                <p style={{ color: '#aaa', fontSize: '0.85rem' }}>
                  Requested: {selected.requested_credits} · Phone: {selected.contact_phone || '—'} · Email: {selected.contact_email || '—'}
                </p>
                <div className="mock-credits-thread">
                  {(selected.messages || []).map((msg) => (
                    <div key={msg.id} className={`mock-credits-msg mock-credits-msg-${msg.author_role === 'admin' ? 'admin' : 'user'}`}>
                      <div className="mock-credits-msg-role">{msg.author_role === 'admin' ? 'Admin' : 'User'}</div>
                      <div className="mock-credits-msg-body">{msg.body}</div>
                      <div className="mock-credits-msg-time">{formatDate(msg.created_at)}</div>
                      {msg.attachments?.map((att) => renderAttachment(selected.id, att))}
                    </div>
                  ))}
                </div>

                {selected.status === 'open' ? (
                  <>
                    <div className="mock-credits-reply-box">
                      <div className="mock-credits-form-group">
                        <label>Reply to user</label>
                        <textarea
                          value={replyBody}
                          onChange={(e) => setReplyBody(e.target.value)}
                          placeholder="e.g. Please send your payment receipt…"
                        />
                      </div>
                      <input type="file" accept="image/*,.pdf" onChange={(e) => setReplyFile(e.target.files?.[0] ?? null)} />
                    </div>

                    <div className="mock-credit-admin-actions">
                      <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={handleReply} disabled={submitting}>
                        Send reply (await user)
                      </button>
                      <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={() => { setShowApproveForm(!showApproveForm); setShowRejectForm(false); }}>
                        {React.createElement(FiCheckCircle as React.ComponentType<{ size?: number }>, { size: 16 })}
                        Approve & add credits
                      </button>
                      <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => { setShowRejectForm(!showRejectForm); setShowApproveForm(false); }}>
                        {React.createElement(FiXCircle as React.ComponentType<{ size?: number }>, { size: 16 })}
                        Reject
                      </button>
                    </div>

                    {showApproveForm ? (
                      <div className="mock-credit-admin-approve-form">
                        <label>Credits to grant</label>
                        <input type="number" min={1} max={100} value={grantedCredits} onChange={(e) => setGrantedCredits(Number(e.target.value))} />
                        <label>Note to user</label>
                        <textarea value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)} placeholder="Payment confirmed. Credits added." />
                        <span />
                        <button type="button" className="mock-credits-btn mock-credits-btn-primary" onClick={handleApprove} disabled={submitting}>
                          Confirm approval
                        </button>
                      </div>
                    ) : null}

                    {showRejectForm ? (
                      <div className="mock-credit-admin-approve-form">
                        <label>Rejection reason</label>
                        <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Required — shown to user" />
                        <span />
                        <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={handleReject} disabled={submitting}>
                          Confirm rejection
                        </button>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="mock-credits-alert mock-credits-alert-info">
                    Closed — {selected.status}
                    {selected.granted_credits != null ? ` · ${selected.granted_credits} credits granted` : ''}
                    {selected.admin_resolution_note ? `: ${selected.admin_resolution_note}` : ''}
                  </div>
                )}
              </div>
              <div className="mock-credits-modal-footer">
                <button type="button" className="mock-credits-btn mock-credits-btn-secondary" onClick={() => setSelected(null)}>
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default MockCreditManagement;
